import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { Button, Table, Tag, Tooltip, Row, Tabs, Space as AntSpace, message } from 'antd';
import {
  UploadOutlined, EyeOutlined, DeleteOutlined,
  DownloadOutlined, EditOutlined, ExportOutlined,
  UsbOutlined, HddOutlined
} from '@ant-design/icons';
import { ColumnProps } from 'antd/lib/table';

import HwzComponent from '../../common/component/HwzComponent';
import Pager from '../../common/model/base/Pager';
import './Listview.less';
import Lang from '../../common/model/global/Lang';
import SortDirection from '../../common/model/base/SortDirection';
import Log, { LogOperation, LogTargetType } from '../../common/model/log/log';

import TableEmpty from '../widget/TableEmpty';
import DateUtil from '../../common/util/DateUtil';
import FilterPanel from '../widget/filter/FilterPanel';
import Space from '../../common/model/space/Space';
import SpaceMember from '../../common/model/space/member/SpaceMember';

import RemovableLogs from '../removable/logs/RemovableLogs';
import Moon from '../../common/model/global/Moon';

const { TabPane } = Tabs;

interface IProps extends RouteComponentProps {
  spaceUuid?: string;
}

interface IState {
  activeTab: 'local' | 'removable';
  exportLoading: boolean;
  cleanLoading: boolean;
}

export default class List extends HwzComponent<IProps, IState> {
  isInSpace = !!this.props.spaceUuid;
  spaceUuid = "";
  space = new Space();
  spaceMember = new SpaceMember();
  searchText: string | null = null;
  localPager = new Pager<Log>(this, Log, 20);

  constructor(props: IProps) {
    super(props);
    const preference = Moon.getSingleton().preference;
    const ctrlMod = preference?.ctrlMod || 0;
    let defaultTab: 'local' | 'removable' = 'local';
    if (ctrlMod === 3) defaultTab = 'removable';

    this.state = {
      activeTab: defaultTab,
      exportLoading: false,
      cleanLoading: false,
    };
  }

  initSpace() {
    if (this.props.spaceUuid) {
      this.space.uuid = this.props.spaceUuid;
      this.space.httpDetail(() => this.updateUI());
      this.spaceMember.httpMine(this.space.uuid, () => this.updateUI());
    } else {
      this.space.uuid = "";
    }
  }

  handleNavigation = (operationType?: string) => {
    if (this.state.activeTab === 'local') {
      this.localPager.setFilterValue('operation', operationType || '');
      this.refreshLocalPager();
    }
  };

  refreshLocalPager() {
    if (!this.localPager.getCurrentSortFilter()) {
      this.localPager.setFilterValue('orderCreateTime', SortDirection.DESC);
      this.localPager.setFilterValue('orderDir', SortDirection.DESC);
    }
    this.localPager.httpList();
  }

  async componentDidMount() {
    this.initSpace();
    this.spaceUuid = "";
    this.localPager.enableHistory();
    if (this.state.activeTab === 'local') this.refreshLocalPager();
  }

  search() {
    if (this.state.activeTab === 'local') {
      this.localPager.page = 0;
      this.refreshLocalPager();
    }
  }

  handleTabChange = (activeKey: string) => {
    this.setState({ activeTab: activeKey as 'local' | 'removable' }, () => {
      if (activeKey === 'local' && this.localPager.data.length === 0) {
        this.refreshLocalPager();
      }
    });
  };

  getOperationText(operation: string): string {
    return Log.prototype.getOperationText(operation);
  }

  getTargetTypeText(targetType: string): string {
    return Log.prototype.getTargetTypeText(targetType);
  }

  getOperationIcon(operation: string): React.ReactNode {
    const iconMap: { [key: string]: React.ReactNode } = {
      [LogOperation.UPLOAD]: <UploadOutlined />,
      [LogOperation.DELETE]: <DeleteOutlined />,
      [LogOperation.SOFT_DELETE]: <DeleteOutlined />,
      [LogOperation.RECOVERY]: <EyeOutlined />,
      [LogOperation.DOWNLOAD]: <DownloadOutlined />,
      [LogOperation.RENAME]: <EditOutlined />,
      [LogOperation.MOVE]: <EditOutlined />,
      [LogOperation.COPY]: <DeleteOutlined />,
      [LogOperation.CREATE_DIRECTORY]: <DeleteOutlined />,
    };
    return iconMap[operation] || <EyeOutlined />;
  }

  // 构建导出参数（复用当前筛选条件）
  buildExportParams(): any {
    const params: any = {};
    const filterKeys = [
      'operation', 'targetType', 'targetName', 'username', 'spaceUuid',
      'ip', 'keyword', 'startTime', 'endTime', 'orderCreateTime', 'orderDir'
    ];
    filterKeys.forEach(key => {
      const value = this.localPager.getFilterValue(key);
      if (value !== null && value !== '' && value !== undefined) {
        params[key] = value;
      }
    });
    return params;
  }

  // 导出日志
  handleExport = () => {
    this.setState({ exportLoading: true });
    const params = this.buildExportParams();
    const log = new Log();
    log.httpExport(params, () => {
      this.setState({ exportLoading: false });
      message.success(Lang.t('log.exportSuccess'));
    }, (error: any) => {
      this.setState({ exportLoading: false });
      message.error(Lang.t('log.exportFailed'));
      console.error('导出失败:', error);
    });
  };

  // 清理日志（默认90天前）
  handleClean = () => {
    const keepDays = 90;
    let confirmMessage = Lang.t('log.cleanConfirm');
    confirmMessage = confirmMessage.replace('{keepDays}', keepDays.toString());
    if (!window.confirm(confirmMessage)) return;

    this.setState({ cleanLoading: true });
    const log = new Log();
    log.httpClean(keepDays, (response: any) => {
      this.setState({ cleanLoading: false });
      message.success(response.message || Lang.t('log.cleanSuccess'));
      this.refreshLocalPager(); // 刷新列表
    }, (error: any) => {
      this.setState({ cleanLoading: false });
      message.error(Lang.t('log.cleanFailed'));
      console.error('清理失败:', error);
    });
  };

  renderLocalLogColumns(): ColumnProps<Log>[] {
    return [
      {
        title: Lang.t('log.operation'),
        dataIndex: 'operation',
        render: (text: string, record: Log): React.ReactNode => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {this.getOperationIcon(record.operation)}
            <span>{this.getOperationText(record.operation)}</span>
          </div>
        ),
      },
      {
        title: Lang.t('log.target'),
        render: (text: any, record: Log): React.ReactNode => (
          <div>
            <div>
              {this.getTargetTypeText(record.targetType)}
              {record.targetName && `: ${record.targetName}`}
            </div>
          </div>
        ),
      },
      {
        title: Lang.t('log.user'),
        render: (text: any, record: Log): React.ReactNode => <div>{record.username}</div>,
      },
      {
        title: Lang.t('log.space'),
        render: (text: any, record: Log): React.ReactNode => <div>{record.spaceName}</div>,
      },
      {
        title: Lang.t('log.detail'),
        dataIndex: 'detail',
        render: (text: string, record: Log): React.ReactNode => (
          <Tooltip title={record.detail}>
            <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {record.detail}
            </div>
          </Tooltip>
        ),
      },
      {
        title: Lang.t('log.ip'),
        dataIndex: 'ip',
        render: (text: string, record: Log): React.ReactNode => <div>{record.ip}</div>,
      },
      {
        title: Lang.t('log.createTime'),
        dataIndex: 'createTime',
        render: (text: any, record: Log): React.ReactNode => (
          <div>{DateUtil.simpleDateTime(record.createTime)}</div>
        ),
      },
    ];
  }

  render() {
    const { activeTab, exportLoading, cleanLoading } = this.state;
    const currentPager = this.localPager;
    const currentOperation = currentPager.getFilterValue('operation');
    const preference = Moon.getSingleton().preference;
    const ctrlMod = preference?.ctrlMod || 0;
    const showLocalTab = ctrlMod !== 3;

    return (
      <div className="log-list">
        <Row justify="space-between" align="middle" className="mb20">
          <h2>{Lang.t('log.title')}</h2>

          {activeTab === 'local' && showLocalTab && (
            <AntSpace>
              <Button
                type={currentOperation === LogOperation.UPLOAD ? 'primary' : 'default'}
                icon={<UploadOutlined />}
                onClick={() => this.handleNavigation(LogOperation.UPLOAD)}
              >
                {Lang.t('log.upload')}
              </Button>
              <Button
                type={currentOperation === LogOperation.DOWNLOAD ? 'primary' : 'default'}
                icon={<DownloadOutlined />}
                onClick={() => this.handleNavigation(LogOperation.DOWNLOAD)}
              >
                {Lang.t('log.download')}
              </Button>
              <Button
                type={currentOperation === LogOperation.DELETE ? 'primary' : 'default'}
                icon={<DeleteOutlined />}
                onClick={() => this.handleNavigation(LogOperation.DELETE)}
              >
                {Lang.t('log.delete')}
              </Button>
              <Button
                type={currentOperation === LogOperation.RENAME ? 'primary' : 'default'}
                icon={<EditOutlined />}
                onClick={() => this.handleNavigation(LogOperation.RENAME)}
              >
                {Lang.t('log.rename')}
              </Button>
              <Button
                type={!currentOperation ? 'primary' : 'default'}
                icon={<EyeOutlined />}
                onClick={() => this.handleNavigation('')}
              >
                {Lang.t('log.allOperations')}
              </Button>

              {/* 新增导出按钮 */}
              <Button
                type="default"
                icon={<ExportOutlined />}
                loading={exportLoading}
                onClick={this.handleExport}
              >
                {Lang.t('log.export')}
              </Button>

              {/* 新增清理按钮 */}
              <Button
                danger
                icon={<DeleteOutlined />}
                loading={cleanLoading}
                onClick={this.handleClean}
              >
                {Lang.t('log.clean')}
              </Button>
            </AntSpace>
          )}
        </Row>

        <Tabs activeKey={activeTab} onChange={this.handleTabChange}>
          {showLocalTab && (
            <TabPane
              tab={
                <span>
                  <HddOutlined />
                  {Lang.t('log.localLogs')}
                </span>
              }
              key="local"
            >
              <div className="mb20">
                <FilterPanel
                  filters={this.localPager.filters}
                  onChange={this.search.bind(this)}
                />
              </div>
              <Table
                loading={this.localPager.loading}
                dataSource={this.localPager.data}
                columns={this.renderLocalLogColumns()}
                pagination={{
                  current: this.localPager.page + 1,
                  total: this.localPager.totalItems,
                  pageSize: this.localPager.pageSize,
                  hideOnSinglePage: true,
                  showSizeChanger: true,
                  pageSizeOptions: ['20', '50', '100'],
                  showTotal: (total) => `${Lang.t('plugin.total')} ${total} ${Lang.t('plugin.items')}`,
                }}
                onChange={this.localPager.tableOnChange.bind(this.localPager)}
                locale={{
                  emptyText: (
                    <TableEmpty pager={this.localPager} onRefresh={this.refreshLocalPager.bind(this)} />
                  ),
                }}
                rowKey="uuid"
              />
            </TabPane>
          )}
          <TabPane
            tab={
              <span>
                <UsbOutlined />
                {Lang.t('log.removableLogs')}
              </span>
            }
            key="removable"
          >
            <RemovableLogs {...this.props} />
          </TabPane>
        </Tabs>
      </div>
    );
  }
}